/**
 * ops キュー表の列ソート。**現在の絞り込み（ステータスタブ / サービス）で得た行の並びだけ**を変える純粋な
 * 写像で、取得条件には一切触れない（絞り込みは呼び出し側の query key が持つ）。
 *
 * ここが持つのは「列 → ソート値」の写像だけで、比較そのものは reliability の `compareTableValues` を
 * 再利用する（null は方向に関わらず末尾、という規則を 2 箇所で実装しないため）。SLO 値も
 * `@cortex/ops-core` の `computeOpsSla` が返す構造化ステータスを符号付き営業日へ畳むだけで、
 * 目標営業日の閾値は持たない（ops-core が単一 source）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops キュー表の ID / 起票 / 緊急度 / SLO 列のソート。SLO は残り営業日の符号付き数値（超過ほど小さい）で昇順＝逼迫順。SLA 対象外（完了・対象外カテゴリ）と No 非数値の行は方向に関わらず末尾へ沈める
 * @graph-connects none
 */
import { compareTableValues, type SortDir } from './table-sort.js';
import { computeOpsSla, opsDisplayNo } from '@cortex/ops-core';
import type { OpsRequest, OpsUrgency } from '../types/ops.js';

/** ソート可能な列（対応者 / 進捗は既に絞り込み手段があるため対象外）。 @graph-connects none */
export type OpsQueueSortKey = 'id' | 'submitted' | 'urgency' | 'slo';

/** 緊急度の並び順（即時 → 当日 → 通常）。昇順で緊急なものが先頭に来る。 @graph-connects none */
const URGENCY_RANK: Record<OpsUrgency, number> = { 即時: 0, 当日: 1, 通常: 2 };

/** 起票日時が Date として読めるか（`''` / "未定" 等のフリーテキストは false）。 @graph-connects none */
function isParsableDate(iso: string): boolean {
  return !Number.isNaN(new Date(iso).getTime());
}

/**
 * SLO 列のソート値（残り営業日の符号付き数値）。超過は負・当日締めは 0・残ありは正で、昇順が
 * そのまま逼迫順になる。SLA 対象外（完了 / 対象外カテゴリ）は null を返して末尾へ沈める。
 * @graph-connects computeOpsSla [calls] SLA ステータス（残り / 超過営業日）の算出
 * @graph-connects isParsableDate [calls] 起票日時が Date として読めるかの判定
 */
function sloSortValue(req: OpsRequest, now: Date | undefined): number | null {
  // 起票日時が日付として読めない行は SLA を算出できない（computeOpsSla が Intl 経由で
  // RangeError: Invalid time value を投げる）。空文字だけでなく、BQ の SAFE.PARSE_DATE が
  // 落ちて request_date の生値（"未定" 等のフリーテキスト）がそのまま来る場合も含むため、
  // 文字列比較ではなく Date として妥当かで判定する。起票列と同じく比較不能として末尾へ送る。
  if (!isParsableDate(req.submittedAt)) return null;
  const { status } = computeOpsSla(req, now);
  switch (status.kind) {
    case 'over':
      return -status.days;
    case 'left':
      return status.days;
    case 'dueToday':
      return 0;
    default:
      return null;
  }
}

/**
 * 依頼を列ごとのソート値へ写す。比較不能な値（No 非数値・起票日なし・未知の緊急度・SLA 対象外）は
 * null を返し、`compareTableValues` の規則で方向に関わらず末尾へ送る。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 依頼を ID / 起票 / 緊急度 / SLO のソート値へ写す。ID は数値 No（"r12" 等の非数値は null）、起票は ISO 文字列、緊急度は即時=0 の順位、SLO は残り営業日
 * @graph-connects opsDisplayNo [calls] id（slug:No）から比較用の表示 No を取り出す
 * @graph-connects isParsableDate [calls] 起票日時が Date として読めるかの判定
 * @graph-connects sloSortValue [calls] SLO 列のソート値（符号付き残り営業日）の算出
 */
export function opsQueueSortValue(
  req: OpsRequest,
  key: OpsQueueSortKey,
  now?: Date,
): string | number | null {
  switch (key) {
    case 'id': {
      // 表示 No は legacy のサービス別採番と Firestore 発番（No >= 10000）が混在する。数値として比較し、
      // 空 No 行の "r<row_number>" 等は数値化できないので末尾へ送る。
      const no = Number.parseInt(opsDisplayNo(req.id), 10);
      return Number.isFinite(no) ? no : null;
    }
    case 'submitted':
      return isParsableDate(req.submittedAt) ? req.submittedAt : null;
    case 'urgency':
      return URGENCY_RANK[req.urgency];
    default:
      return sloSortValue(req, now);
  }
}

/**
 * 読み込み済みの行を指定列で並び替える。`key` が null なら取得順（サーバの起票降順）のまま返す。
 * 並びだけを変える純粋関数で、入力配列は破壊しない。同値は元の順序を保つ（Array#sort は安定）。
 *
 * ソート値は**行ごとに 1 度だけ**求めて比較関数の外に出す（decorate-sort-undecorate）。SLO 値の算出は
 * `computeOpsSla` → `getJstYmd` 経由で `Intl.DateTimeFormat` を作るため、比較関数の中で呼ぶと
 * O(n log n) 回に膨らむ。スクロールで行が積み上がるキューでは、次ページが来るたび全行を並べ替え直す
 * ので、この差が体感できるメインスレッド占有になる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops キューの読み込み済み行を ID / 起票 / 緊急度 / SLO で並び替える。列未選択なら取得順のまま。絞り込み結果の並びだけを変え、取得条件には触れない
 * @graph-connects opsQueueSortValue [calls] 行を列ごとのソート値へ写す
 * @graph-connects compareTableValues [calls] null 末尾・数値/文字列判定つきの比較
 */
export function sortOpsQueueRows(
  rows: readonly OpsRequest[],
  key: OpsQueueSortKey | null,
  dir: SortDir,
  now?: Date,
): OpsRequest[] {
  if (key === null) return [...rows];
  return rows
    .map((row) => ({ row, value: opsQueueSortValue(row, key, now) }))
    .sort((a, b) => compareTableValues(a.value, b.value, dir))
    .map((entry) => entry.row);
}
